import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiFetch from '../utils/apiFetch';

/**
 * 用户中心页面
 * 功能：修改用户基本信息、修改密码
 */
export default function UserCenter() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  
  // 用户信息表单状态
  const [userForm, setUserForm] = useState({
    username: '',
    realName: '',
    email: ''
  });
  
  // 密码修改表单状态
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // 页面初始化时获取用户信息
  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    
    if (!token || !username) {
      navigate('/login');
      return;
    }
    
    // 获取用户详细信息
    fetchUserInfo();
  }, [navigate]);
  
  // 获取用户详细信息
  const fetchUserInfo = async () => {
    try {
      const response = await apiFetch('/api/user/profile');
      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        setUserForm({
          username: data.user.username || '',
          realName: data.user.real_name || '',
          email: data.user.email || ''
        });
      } else {
        setMessage(data.error || '获取用户信息失败');
        setMessageType('error');
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      setMessage('网络错误，请稍后重试');
      setMessageType('error');
    }
  };
  
  // 更新用户信息
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!userForm.username.trim()) {
      setMessage('用户名不能为空');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiFetch('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({
          username: userForm.username,
          realName: userForm.realName,
          email: userForm.email
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage('用户信息更新成功');
        setMessageType('success');
        // 更新localStorage中的用户名
        localStorage.setItem('username', userForm.username);
        // 重新获取用户信息
        fetchUserInfo();
      } else {
        setMessage(data.error || '更新失败');
        setMessageType('error');
      }
    } catch (error) {
      console.error('更新用户信息失败:', error);
      setMessage('网络错误，请稍后重试');
      setMessageType('error');
    }
    setLoading(false);
  };
  
  // 修改密码
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword) {
      setMessage('请输入当前密码');
      setMessageType('error');
      return;
    }
    
    if (!passwordForm.newPassword) {
      setMessage('请输入新密码');
      setMessageType('error');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      setMessage('新密码长度至少6位');
      setMessageType('error');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage('两次输入的新密码不一致');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiFetch('/api/user/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage('密码修改成功');
        setMessageType('success');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setMessage(data.error || '密码修改失败');
        setMessageType('error');
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      setMessage('网络错误，请稍后重试');
      setMessageType('error');
    }
    setLoading(false);
  };
  
  const containerStyle = {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px'
  };
  
  const cardStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  };
  
  const headerStyle = {
    background: '#1976d2',
    color: '#fff',
    padding: '20px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold'
  };
  
  const contentStyle = {
    padding: '30px'
  };
  
  const sectionStyle = {
    marginBottom: '40px',
    padding: '20px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px'
  };
  
  const titleStyle = {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#333',
    borderBottom: '2px solid #1976d2',
    paddingBottom: '10px'
  };
  
  const formRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '15px'
  };
  
  const labelStyle = {
    marginBottom: '5px',
    fontWeight: '500',
    color: '#555'
  };
  
  const inputStyle = {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };
  
  const buttonStyle = {
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };
  
  const disabledButtonStyle = {
    ...buttonStyle,
    background: '#ccc',
    cursor: 'not-allowed'
  };
  
  const messageStyle = {
    padding: '10px',
    margin: '15px 0',
    borderRadius: '4px',
    textAlign: 'center',
    fontWeight: '500'
  };
  
  const successMessageStyle = {
    ...messageStyle,
    background: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb'
  };
  
  const errorMessageStyle = {
    ...messageStyle,
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb'
  };
  
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          用户中心
        </div>
        
        <div style={contentStyle}>
          {message && (
            <div style={messageType === 'success' ? successMessageStyle : errorMessageStyle}>
              {message}
            </div>
          )}
          
          {/* 用户信息部分 */}
          <div style={sectionStyle}>
            <div style={titleStyle}>个人信息</div>
            <form onSubmit={handleUpdateProfile}>
              <div style={formRowStyle}>
                <label style={labelStyle}>用户名</label>
                <input
                  type="text"
                  style={inputStyle}
                  value={userForm.username}
                  onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                  placeholder="请输入用户名"
                />
              </div>
              
              <div style={formRowStyle}>
                <label style={labelStyle}>真实姓名</label>
                <input
                  type="text"
                  style={inputStyle}
                  value={userForm.realName}
                  onChange={(e) => setUserForm({...userForm, realName: e.target.value})}
                  placeholder="请输入真实姓名"
                />
              </div>
              
              <div style={formRowStyle}>
                <label style={labelStyle}>邮箱</label>
                <input
                  type="email"
                  style={inputStyle}
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  placeholder="请输入邮箱地址"
                />
              </div>
              
              <button 
                type="submit" 
                style={loading ? disabledButtonStyle : buttonStyle}
                disabled={loading}
              >
                {loading ? '更新中...' : '更新信息'}
              </button>
            </form>
          </div>
          
          {/* 密码修改部分 */}
          <div style={sectionStyle}>
            <div style={titleStyle}>修改密码</div>
            <form onSubmit={handleChangePassword}>
              <div style={formRowStyle}>
                <label style={labelStyle}>当前密码</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  placeholder="请输入当前密码"
                />
              </div>
              
              <div style={formRowStyle}>
                <label style={labelStyle}>新密码</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  placeholder="请输入新密码（至少6位）"
                />
              </div>
              
              <div style={formRowStyle}>
                <label style={labelStyle}>确认新密码</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  placeholder="请再次输入新密码"
                />
              </div>
              
              <button 
                type="submit" 
                style={loading ? disabledButtonStyle : buttonStyle}
                disabled={loading}
              >
                {loading ? '修改中...' : '修改密码'}
              </button>
            </form>
          </div>
          
          {user && (
            <div style={sectionStyle}>
              <div style={titleStyle}>账户信息</div>
              <p><strong>用户ID:</strong> {user.id}</p>
              <p><strong>角色:</strong> {user.role}</p>
              <p><strong>注册时间:</strong> {new Date(user.created_at).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}